import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Breadcrumb from "../../Components/Navigation/Breadcrumbs/Breadcrumbs";
import { getAllClients } from "../../Services/API/ClientManagement";
import SortableTable from "../../Components/Table/SortableTable";
import UserCell from "../../Components/Table/UserCell";
import AutoCompleteComponent from "../../Components/AutoComplete/AutoComplete";
import { Button, Dialog, Grid } from "@material-ui/core";
import CreateUserModal from "../../Components/Modal/CreateUserModal";
import Signifier from "../../Components/Signifier";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import { Client } from "../../Types/ClientManagement";
import { ActivateUser } from "../../Types/Users";
import { useGetAllUsersQuery } from "../../Queries/UserManagementQueries";
import { usePaginationRequestHook } from "../../Helpers/paginationRequestHook";
import { TablePaginationSettings } from "../../Types/Pagination";

interface UserManagementLocationState {
  message?: string;
}

function UserManagementPage() {
  const useStyles = makeStyles((theme) => ({
    root: {
      width: "90%",
      marginBottom: "50px",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    autocomplete: {
      width: "20%",
      paddingBottom: "20px",
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
    dialog: {
      "& .MuiDialogContent-root": {
        padding: theme.spacing(2),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
      },
      "& .MuiDialogActions-root": {
        paddingTop: theme.spacing(1),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(3),
      },
    },
  }));

  const classes = useStyles();

  const [allClients, setAllClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [createProcessing, setCreateProcessing] = useState(false);
  const location = useLocation<UserManagementLocationState | undefined>();
  const history = useHistory();
  const [arrivalMessage, setArrivalMessage] = useState(location.state?.message ?? "");
  const [showArrivalMessage, setShowArrivalMessage] = useState(!!location.state?.message);
  const [tableData, setTableData] = useState<ActivateUser[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalItems, setTotalItems] = useState<number>(25);
  const [search, setSearchTerm] = useState<string>("");
  const [sortProperty, setSortProperty] = useState<string>("fullName");
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  axios.defaults.headers.common["client"] = selectedClient?.id;

  const [paginationRequest] = usePaginationRequestHook(page, pageSize, search, sortProperty, sortAscending);
  const { data: usersResponse, isLoading: usersResponseLoading } = useGetAllUsersQuery(
    paginationRequest,
    selectedClient?.id
  );

  const columns = [
    { label: "Name", propName: "fullName" },
    { label: "Email", propName: "emailAddress" },
    { label: "Date Created", propName: "dateCreated" },
    { label: "Date Last Updated", propName: "dateLastUpdated" },
  ];

  const paginationSettings: TablePaginationSettings = {
    enabled: true,
    currentPage: usersResponse ? usersResponse.page : page,
    pageSizeOptions: [25, 50, 100],
    pageSize: usersResponse ? usersResponse.pageSize : pageSize,
    setCurrentPage: setPage,
    setPageSize: setPageSize,
    setSearchValue: setSearchTerm,
    setSortProperty: setSortProperty,
    setSortAscending: setSortAscending,
  };

  const convertDate = (date: string) => {
    var newDate = new Date(date).toLocaleDateString("en-US");
    return newDate;
  };

  const compare = (a: any, b: any) => {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  };

  // Clear the redirect's message so a refresh or back-navigation doesn't show it again.
  useEffect(() => {
    if (location.state?.message) {
      setArrivalMessage(location.state.message);
      setShowArrivalMessage(true);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  useEffect(() => {
    getAllClients().then((response: Client[]) => {
      response.sort((a, b) => {
        const order = compare(a.shortName, b.shortName);
        return order;
      });
      setAllClients(response);
    });
  }, []);

  useEffect(() => {
    if (usersResponse?.items) {
      const updatedItems = usersResponse.items.map((user) => ({
        ...user,
        fullName: `${user.firstName} ${user.lastName}`,
        dateCreated: convertDate(user.dateCreated),
        dateLastUpdated: convertDate(user.dateLastUpdated),
      }));
      setTotalItems(usersResponse?.totalItems);
      setTableData(updatedItems);
    }
  }, [usersResponse, setTableData]);

  return (
    <>
      {arrivalMessage ? (
        <Signifier
          showSignifier={showArrivalMessage}
          setShowSignifier={setShowArrivalMessage}
          message={arrivalMessage}
          error={false}
          errorMessage={undefined}
        />
      ) : null}
      <div className="page-container">
        <Breadcrumb />
        <h1 className="page-title">Users</h1>
        <div className={classes.root}>
          <div className={classes.autocomplete}>
            <AutoCompleteComponent
              dropdownItems={allClients}
              propertyName="shortName"
              label="All Clients"
              onChange={(e, value) => setSelectedClient(value)}
              currValue={undefined}
            />
          </div>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Button
                color="primary"
                style={{ color: "white", marginBottom: "10px" }}
                type="submit"
                variant="contained"
                onClick={() => setOpenModal(true)}
              >
                Add User
              </Button>
            </Grid>
          </Grid>
          <SortableTable
            columns={columns}
            tableData={tableData}
            rowComponent={UserCell}
            loading={usersResponseLoading}
            hideSearch={false}
            pagination={paginationSettings}
            totalItemCount={totalItems}
            defaultSortIndex={0}
            setTableData={undefined}
            reverse={false}
          />
        </div>
      </div>
      <Dialog
        maxWidth="xl"
        open={openModal}
        onClose={(event, reason) => {
          if (!createProcessing && reason === "backdropClick") {
            setOpenModal(false);
          }
        }}
      >
        <CreateUserModal openModal={setOpenModal} setCreateProcessing={setCreateProcessing} />
      </Dialog>
    </>
  );
}

export default UserManagementPage;
